"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { BANNER_DEFAULT_RESOLUTION } from "@/lib/banners/constants";
import { BannerResolutionKey, PublicBannerItem, PublicBannersResponse } from "@/lib/banners/types";

type BannerBreakpoint = "mobile" | "tablet" | "desktop";

type BannerBreakpointsConfig = {
  mobileMaxWidth?: number;
  tabletMaxWidth?: number;
};

export type BannerResolutionStrategy = {
  mode?: "responsive" | "fixed";
  fixedResolutionKey?: BannerResolutionKey;
  map?: Partial<Record<BannerBreakpoint, BannerResolutionKey>>;
  breakpoints?: BannerBreakpointsConfig;
};

const DEFAULT_BREAKPOINTS = {
  mobileMaxWidth: 767,
  tabletMaxWidth: 1024,
} as const;

const DEFAULT_RESOLUTION_MAP: Record<BannerBreakpoint, BannerResolutionKey> = {
  mobile: "mobile",
  tablet: "tablet",
  desktop: "desktop",
};

const getViewportBreakpoint = (width: number, breakpoints?: BannerBreakpointsConfig): BannerBreakpoint => {
  const mobileMaxWidth = breakpoints?.mobileMaxWidth ?? DEFAULT_BREAKPOINTS.mobileMaxWidth;
  const tabletMaxWidth = breakpoints?.tabletMaxWidth ?? DEFAULT_BREAKPOINTS.tabletMaxWidth;

  if (width <= mobileMaxWidth) {
    return "mobile";
  }

  if (width <= tabletMaxWidth) {
    return "tablet";
  }

  return "desktop";
};

const resolveStrategyResolutionKey = (
  viewportBreakpoint: BannerBreakpoint,
  strategy?: BannerResolutionStrategy,
): BannerResolutionKey => {
  if (strategy?.mode === "fixed" && strategy.fixedResolutionKey) {
    return strategy.fixedResolutionKey;
  }

  const map = {
    ...DEFAULT_RESOLUTION_MAP,
    ...(strategy?.map ?? {}),
  };

  return map[viewportBreakpoint];
};

const useViewportBreakpoint = (breakpoints?: BannerBreakpointsConfig) => {
  const [breakpoint, setBreakpoint] = useState<BannerBreakpoint | null>(null);

  useEffect(() => {
    const updateBreakpoint = () => {
      setBreakpoint(getViewportBreakpoint(window.innerWidth, breakpoints));
    };

    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);

    return () => {
      window.removeEventListener("resize", updateBreakpoint);
    };
  }, [breakpoints, breakpoints?.mobileMaxWidth, breakpoints?.tabletMaxWidth]);

  return {
    breakpoint,
    isReady: breakpoint !== null,
  };
};

const isFixedResolutionStrategy = (strategy?: BannerResolutionStrategy) =>
  strategy?.mode === "fixed" && !!strategy.fixedResolutionKey;

export const usePublicBanner = ({
  slotKey,
  locale,
  strategy,
  enabled = true,
}: {
  slotKey: string;
  locale: string;
  strategy?: BannerResolutionStrategy;
  enabled?: boolean;
}) => {
  const usesViewportResolution = !isFixedResolutionStrategy(strategy);
  const { breakpoint: viewportBreakpoint, isReady: isViewportReady } = useViewportBreakpoint(
    usesViewportResolution ? strategy?.breakpoints : undefined,
  );

  const resolutionKey = useMemo(() => {
    if (isFixedResolutionStrategy(strategy)) {
      return strategy!.fixedResolutionKey!;
    }

    if (!isViewportReady || viewportBreakpoint === null) {
      return null;
    }

    return resolveStrategyResolutionKey(viewportBreakpoint, strategy);
  }, [isViewportReady, strategy, viewportBreakpoint]);

  const isQueryEnabled =
    enabled && !!slotKey && resolutionKey !== null && (usesViewportResolution ? isViewportReady : true);

  const query = useQuery({
    queryKey: ["public-banner", slotKey, locale, resolutionKey],
    queryFn: async () => {
      const queryString = new URLSearchParams({
        slotKey,
        locale,
        resolutionKey: resolutionKey!,
      });

      const response = await fetch(`/api/banners/public?${queryString.toString()}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch banner for slot "${slotKey}"`);
      }

      const payload = (await response.json()) as PublicBannersResponse;
      if (!payload.success || !Array.isArray(payload.data?.result)) {
        return [] as PublicBannerItem[];
      }

      return payload.data.result;
    },
    enabled: isQueryEnabled,
  });

  const banners = (query.data ?? []) as PublicBannerItem[];

  return {
    ...query,
    banners,
    banner: banners[0] ?? null,
    hasMultipleBanners: banners.length > 1,
    resolutionKey: resolutionKey ?? strategy?.fixedResolutionKey ?? BANNER_DEFAULT_RESOLUTION,
    viewportBreakpoint: viewportBreakpoint ?? "desktop",
    isViewportReady: usesViewportResolution ? isViewportReady : true,
  };
};
